import type { DiagnosticCategory, DiagnosticQuestionDefinition, DiagnosticScaleValue } from './types';

export const TURNSTILE_SITE_KEY =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TURNSTILE_SITE_KEY) || '';

export const SCALE_LABELS: Record<DiagnosticScaleValue, string> = {
  0: 'Ainda não fazemos.',
  1: 'Fazemos de vez em quando.',
  2: 'Fazemos com alguma consistência.',
  3: 'Fazemos, acompanhamos e conseguimos provar.',
};

export const DIAGNOSTIC_CATEGORIES: DiagnosticCategory[] = [
  {
    key: 'positioning',
    name: 'Posicionamento',
    questions: [
      {
        text: 'Hoje, você consegue explicar em uma frase qual tipo de empresário seu escritório atende melhor?',
        options: {
          0: 'Não consigo definir.',
          1: 'Tenho uma ideia, mas ainda é ampla.',
          2: 'Consigo explicar, mas nem sempre comunico.',
          3: 'Está definido e aparece claramente na comunicação.',
        },
      },
      {
        text: 'Como seus principais serviços são apresentados aos empresários?',
        options: {
          0: 'Como obrigações contábeis e fiscais que toda empresa precisa cumprir.',
          1: 'Como serviços tradicionais, com algumas tentativas de oferecer consultoria.',
          2: 'Como soluções para problemas específicos, mas o cliente nem sempre enxerga o valor.',
          3: 'Como soluções diretas para gargalos financeiros e de gestão que o cliente reconhece e valoriza.',
        },
      },
      {
        text: 'Quando um cliente em potencial compara seu escritório com concorrentes, o que pesa na decisão dele?',
        options: {
          0: 'O preço mais baixo; ele escolhe quem cobrar menos.',
          1: 'O valor dos honorários, exigindo concessão de descontos para fechar.',
          2: 'Nossa qualidade técnica, mas ele ainda tenta barganhar o preço.',
          3: 'Nossa especialidade e método de trabalho, sem leilão de honorários.',
        },
      },
    ] as DiagnosticQuestionDefinition[],
  },
  {
    key: 'founder_authority',
    name: 'Autoridade do fundador',
    questions: [
      {
        text: 'Com que frequência o fundador ou sócio aparece nos canais para orientar e educar empresários?',
        options: {
          0: 'Não aparece nos canais do escritório.',
          1: 'Aparece apenas em ocasiões pontuais ou raras.',
          2: 'Aparece com alguma frequência, mas sem constância.',
          3: 'Aparece de forma recorrente e planejada.',
        },
      },
      {
        text: 'Que tipo de orientação o fundador compartilha com o mercado?',
        options: {
          0: 'Não compartilha orientações ou posicionamentos.',
          1: 'Apenas reproduz notícias genéricas e mudanças na legislação.',
          2: 'Explica regras tributárias, mas com termos técnicos difíceis para o empresário.',
          3: 'Ensina como superar problemas práticos de gestão e tomada de decisão no negócio.',
        },
      },
      {
        text: 'Como o escritório demonstra que realmente entrega os resultados prometidos?',
        options: {
          0: 'Não mostramos provas, processos ou depoimentos.',
          1: 'Guardamos elogios no WhatsApp, mas não usamos na comunicação externa.',
          2: 'Divulgamos depoimentos isolados, sem explicar a transformação ocorrida.',
          3: 'Apresentamos casos reais, dados de impacto e depoimentos que comprovam o método.',
        },
      },
    ] as DiagnosticQuestionDefinition[],
  },
  {
    key: 'digital_assets',
    name: 'Perfil e site',
    questions: [
      {
        text: 'Ao conhecer o perfil do escritório nas redes sociais, o empresário entende a proposta?',
        options: {
          0: 'Não fica claro quem atendemos nem o que resolvemos.',
          1: 'Ele entende que somos uma contabilidade, mas a mensagem é genérica.',
          2: 'A proposta está clara, mas o próximo passo para contato ainda gera dúvida.',
          3: 'Ele entende quem ajudamos, o que resolvemos e sabe exatamente como avançar.',
        },
      },
      {
        text: 'Ao acessar o site oficial do escritório, qual é a experiência do visitante?',
        options: {
          0: 'Não temos site ativo ou a página está desatualizada.',
          1: 'O site é institucional básico, listando apenas os serviços contábeis comuns.',
          2: 'O site é moderno, mas ainda faltam provas reais e detalhes de como funciona o atendimento.',
          3: 'O site confirma o posicionamento, exibe casos de sucesso e conduz ao contato de forma simples.',
        },
      },
      {
        text: 'O que acontece quando um interessado decide entrar em contato com o escritório?',
        options: {
          0: 'Encontra barreiras, como formulários extensos ou canais sem resposta rápida.',
          1: 'Precisa buscar onde clicar ou passar por mais de uma etapa para falar com alguém.',
          2: 'Consegue chamar no WhatsApp, mas precisa preencher dados antes de iniciar o diálogo.',
          3: 'Inicia a conversa com um clique direto, sem atrito e no canal de sua preferência.',
        },
      },
    ] as DiagnosticQuestionDefinition[],
  },
  {
    key: 'distribution',
    name: 'Distribuição',
    questions: [
      {
        text: 'Como são escolhidos os temas dos conteúdos e das mensagens do escritório?',
        options: {
          0: 'Não produzimos conteúdos nem mensagens informativas.',
          1: 'Copiamos ideias de outros perfis ou usamos posts genéricos de agências.',
          2: 'Abordamos prazos e obrigações do calendário fiscal do mês.',
          3: 'Nascem de dúvidas, dores e conversas reais que temos com nossos clientes no dia a dia.',
        },
      },
      {
        text: 'Quais caminhos o escritório utiliza para atrair novos clientes?',
        options: {
          0: 'Dependemos exclusivamente da indicação passiva de conhecidos.',
          1: 'Publicamos nas redes sociais, mas as indicações continuam sendo a única fonte.',
          2: 'Combinamos indicações com parceiros ou participações em eventos de forma esporádica.',
          3: 'Trabalhamos de forma ativa e combinada: conteúdo, indicações estruturadas, eventos e parceiros.',
        },
      },
      {
        text: 'Se o escritório investe recursos em anúncios ou divulgação, qual é o controle dos resultados?',
        options: {
          0: 'Não investimos recursos financeiros em divulgação ou anúncios.',
          1: 'Já investimos, mas não conseguimos saber se gerou novas conversas.',
          2: 'Sabemos quantos contatos chegaram, mas não temos certeza de quantos viraram clientes.',
          3: 'Identificamos claramente quais canais e campanhas geram reuniões e contratos assinados.',
        },
      },
    ] as DiagnosticQuestionDefinition[],
  },
  {
    key: 'commercial_conversation',
    name: 'Conversa comercial',
    questions: [
      {
        text: 'Como é o primeiro atendimento a quem procura o escritório interessado em serviços?',
        options: {
          0: 'Demoramos para dar retorno e não temos uma pessoa definida para responder.',
          1: 'Respondemos no mesmo dia, mas deixamos a condução da conversa na mão do interessado.',
          2: 'Respondemos rápido, porém o agendamento de uma conversa depende da agenda livre do sócio.',
          3: 'Respondemos prontamente e conduzimos o interessado com segurança para a próxima etapa.',
        },
      },
      {
        text: 'O que acontece antes de o escritório apresentar preço ao interessado?',
        options: {
          0: 'Enviamos o preço com poucas informações.',
          1: 'Fazemos algumas perguntas básicas.',
          2: 'Entendemos o contexto antes de montar a proposta.',
          3: 'Realizamos um diagnóstico de contexto, risco, urgência e prioridade.',
        },
      },
      {
        text: 'Como o escritório gerencia as propostas enviadas e os motivos de fechamento ou perda?',
        options: {
          0: 'Não anotamos; as propostas ficam perdidas em conversas do WhatsApp ou e-mails.',
          1: 'Anotamos em planilhas ou blocos, mas não acompanhamos a taxa de fechamento.',
          2: 'Registramos as propostas em um sistema, mas sem analisar os motivos de perda.',
          3: 'Controlamos em CRM todo o funil: origem do lead, proposta, fechamento e por que perdemos negócios.',
        },
      },
    ] as DiagnosticQuestionDefinition[],
  },
  {
    key: 'customer_experience',
    name: 'Experiência do cliente',
    questions: [
      {
        text: 'Qual é o nível de clareza do cliente sobre o escopo contratado e os prazos de entrega?',
        options: {
          0: 'O cliente não sabe o que contratou e surgem atritos constantes sobre o que é cobrado à parte.',
          1: 'Há um contrato assinado, mas o cliente frequentemente questiona prazos e limites do serviço.',
          2: 'O cliente conhece a rotina básica, mas ainda ocorrem dúvidas pontuais sobre demandas extras.',
          3: 'Ele sabe exatamente o que está incluso, o custo de trabalhos adicionais e quando terá retorno.',
        },
      },
      {
        text: 'Como funciona o processo de entrada e integração (onboarding) de um novo cliente?',
        options: {
          0: 'Não há processo estruturado; solicitamos documentos à medida que a necessidade surge.',
          1: 'Enviamos uma lista padrão de documentos, mas o cliente fica inseguro nos primeiros meses.',
          2: 'Temos etapas de implantação organizadas, mas a condução ainda exige muito tempo do fundador.',
          3: 'Seguimos um roteiro padronizado de boas-vindas, alinhamento de expectativas e início seguro da operação.',
        },
      },
      {
        text: 'Como o escritório acompanha a satisfação do cliente para evitar cancelamentos?',
        options: {
          0: 'Só sabemos que algo ia mal quando o cliente pede a transferência do escritório.',
          1: 'Percebemos insatisfações no dia a dia, mas só corrigimos quando o cliente reclama formalmente.',
          2: 'Temos uma relação próxima com a maioria, mas sem um canal ou rotina estruturada de avaliação.',
          3: 'Aplicamos avaliações periódicas e tratamos gargalos de imediato, antes de qualquer risco de saída.',
        },
      },
    ] as DiagnosticQuestionDefinition[],
  },
];

export const TOTAL_QUESTIONS_COUNT = 18;
